import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
} from '../../../store/api/inventoryApi';

function LocationsManagement() {
  const navigate = useNavigate();
  const { data: locations, isLoading } = useGetLocationsQuery();
  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();
  const [deleteLocation] = useDeleteLocationMutation();

  const [open, setOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
  });
  const [error, setError] = useState('');

  const handleOpen = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name,
        code: location.code,
        address: location.address || '',
      });
    } else {
      setEditingLocation(null);
      setFormData({ name: '', code: '', address: '' });
    }
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingLocation) {
        await updateLocation({ id: editingLocation.id, ...formData }).unwrap();
      } else {
        await createLocation(formData).unwrap();
      }
      handleClose();
    } catch (err) {
      setError(err?.data?.message || 'Failed to save location');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await deleteLocation(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || 'Failed to delete location');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/inventory')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Manage Locations
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{
            background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
            boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #44A08D 0%, #3BA89F 100%)',
            },
          }}
        >
          Add Location
        </Button>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4} align="center">Loading...</TableCell></TableRow>
                ) : locations?.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No locations found</TableCell></TableRow>
                ) : (
                  locations?.map((loc) => (
                    <TableRow key={loc.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{loc.code}</TableCell>
                      <TableCell>{loc.name}</TableCell>
                      <TableCell>{loc.address || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpen(loc)} color="primary" size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(loc.id)} 
                          color="error" 
                          size="small"
                          disabled={loc.code === 'MAIN'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingLocation ? 'Edit Location' : 'Add New Location'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                fullWidth
                label="Location Code"
                placeholder="e.g. KITCHEN, STORE1"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={editingLocation?.code === 'MAIN'}
              />
              <TextField
                fullWidth
                label="Location Name"
                placeholder="e.g. Kitchen, Main Store"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Address / Description"
                placeholder="Where is this located?"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #44A08D 0%, #3BA89F 100%)',
                },
              }}
            >
              {editingLocation ? 'Save Changes' : 'Create Location'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default LocationsManagement;
